import React, { useMemo } from 'react';

import StatCard from '../ui/StatCard';
import { ClipboardListIcon, PlusIcon, WarningIcon, BankIcon } from '../ui/icons';

// Dashboard roundup for the applications page: a week-at-a-glance stat row that
// favours numbers the team can act on over vanity totals.
//   Active files      - touched (created or updated) in the last 7 days
//   New this week      - created in the last 7 days, with the week-on-week delta
//   Needs attention    - active files left untouched for 14+ days
//   Frollo statements  - files offered the Frollo provider (or both)
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const STALE_MS = 14 * DAY_MS;

const mostRecentTime = (app) => {
    const lastUpdated = new Date(app.last_updated || 0).getTime();
    const created = new Date(app.created || 0).getTime();
    return Math.max(lastUpdated, created);
};

const isArchived = (app) => app.tracking_status === 'archived' || !!app.archived_at;

const ApplicationStats = ({ applications }) => {
    const stats = useMemo(() => {
        const list = applications || [];
        const now = Date.now();
        const weekAgo = now - WEEK_MS;
        const twoWeeksAgo = now - 2 * WEEK_MS;
        const staleBefore = now - STALE_MS;

        let activeThisWeek = 0;
        let newThisWeek = 0;
        let newPrevWeek = 0;
        let needsAttention = 0;
        let frollo = 0;

        list.forEach((app) => {
            const createdTime = new Date(app.created || 0).getTime();

            if (mostRecentTime(app) >= weekAgo) activeThisWeek += 1;
            if (createdTime >= weekAgo) newThisWeek += 1;
            else if (createdTime >= twoWeeksAgo) newPrevWeek += 1;

            if (!isArchived(app) && mostRecentTime(app) < staleBefore) needsAttention += 1;

            const provider = app.bank_statement_provider;
            if (provider === 'frollo' || provider === 'both') frollo += 1;
        });

        return {
            activeThisWeek,
            newThisWeek,
            newDelta: newThisWeek - newPrevWeek,
            needsAttention,
            frollo,
        };
    }, [applications]);

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <StatCard
                tone="purple"
                icon={<ClipboardListIcon className="h-5 w-5" />}
                value={stats.activeThisWeek}
                label="Active files"
                hint="Updated in the last 7 days"
            />
            <StatCard
                tone="blue"
                icon={<PlusIcon className="h-5 w-5" />}
                value={stats.newThisWeek}
                delta={stats.newDelta}
                label="New this week"
                hint="Created in the last 7 days"
            />
            <StatCard
                tone="amber"
                icon={<WarningIcon className="h-5 w-5" />}
                value={stats.needsAttention}
                label="Needs attention"
                hint="Active, untouched 14+ days"
            />
            <StatCard
                tone="green"
                icon={<BankIcon className="h-5 w-5" />}
                value={stats.frollo}
                label="Frollo statements"
                hint="Files using Frollo or both"
            />
        </div>
    );
};

export default ApplicationStats;
